import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Zap, Upload, Database, Filter, MessageSquare, Send, Loader2,
  CheckCircle2, ShieldAlert, ClipboardPaste, CalendarClock, Info,
} from "lucide-react";
import { PageHeader } from "@/pages/Accounts";

export default function QuickSendPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [ctx, setCtx] = useState(null);
  const [ctxError, setCtxError] = useState(null);
  const [accountId, setAccountId] = useState("");

  const [audienceMode, setAudienceMode] = useState("existing"); // "existing" | "upload" | "paste"
  const [contactListId, setContactListId] = useState(sp.get("contact_list_id") || "");
  const [uploadedListId, setUploadedListId] = useState("");
  const [pastedListId, setPastedListId] = useState("");

  const [filters, setFilters] = useState({});
  const [skipFilters, setSkipFilters] = useState(true);
  const [templateId, setTemplateId] = useState("");
  const [variableMapping, setVariableMapping] = useState({});
  const [skipConsent, setSkipConsent] = useState(false);
  const [skipReason, setSkipReason] = useState("");

  // Live estimate (from /reevaluate — no draft creation)
  const [estimate, setEstimate] = useState(null); // { audience_stats, cost_estimate }
  const [estimating, setEstimating] = useState(false);
  const [estimateError, setEstimateError] = useState(null);

  // Scheduling
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleAtLocal, setScheduleAtLocal] = useState(""); // datetime-local

  // Final submit
  const [submitting, setSubmitting] = useState(false);
  const [testMobile, setTestMobile] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    api.get("/quick-send/context")
      .then(r => {
        setCtx(r.data);
        if (r.data.accounts.length && !accountId) setAccountId(r.data.accounts[0].id);
      })
      .catch(e => setCtxError(formatApiError(e)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const account = useMemo(
    () => ctx?.accounts.find(a => a.id === accountId),
    [ctx, accountId],
  );

  const refreshContactLists = useCallback(async () => {
    try {
      const { data } = await api.get("/quick-send/context");
      setCtx(data);
    } catch (e) { /* ignore */ }
  }, []);

  const contactLists = useMemo(
    () => (ctx?.contact_lists || []).filter(c => !accountId || c.account_id === accountId),
    [ctx, accountId],
  );
  const templates = useMemo(
    () => (ctx?.templates || []).filter(t => !accountId || t.account_id === accountId),
    [ctx, accountId],
  );

  const effectiveListId = audienceMode === "upload" ? uploadedListId
                        : audienceMode === "paste"  ? pastedListId
                        : contactListId;
  const selectedList = useMemo(
    () => contactLists.find(c => c.id === effectiveListId),
    [contactLists, effectiveListId],
  );
  const selectedTpl = useMemo(
    () => templates.find(t => t.id === templateId),
    [templates, templateId],
  );

  // ---- Debounced live estimate via /reevaluate ----
  const debounceTimer = useRef(null);
  const doReevaluate = useCallback(async () => {
    if (!effectiveListId || !templateId) {
      setEstimate(null); setEstimateError(null);
      return;
    }
    setEstimating(true); setEstimateError(null);
    try {
      const { data } = await api.post("/quick-send/reevaluate", {
        contact_list_id: effectiveListId,
        template_id: templateId,
        filters: skipFilters ? {} : filters,
        skip_consent_scrub: isAdmin && skipConsent,
        duplicate_suppression_days: 0,
      });
      setEstimate(data);
    } catch (e) {
      setEstimateError(formatApiError(e));
      setEstimate(null);
    } finally {
      setEstimating(false);
    }
  }, [effectiveListId, templateId, filters, skipFilters, skipConsent, isAdmin]);

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(doReevaluate, 350);
    return () => clearTimeout(debounceTimer.current);
  }, [doReevaluate]);

  // ---- Submit (create draft + optional schedule / send) ----
  const willSend = estimate?.audience_stats?.final_recipients ?? 0;
  const readyToSend = !!(effectiveListId && templateId && account && willSend > 0)
    && !(selectedTpl?.category === "service_explicit" && skipConsent && !skipReason.trim());

  const doSend = async () => {
    if (!readyToSend) return;
    // Validate schedule if enabled
    let scheduleIso = null;
    if (scheduleEnabled) {
      if (!scheduleAtLocal) {
        toast.error("Pick a schedule date/time");
        return;
      }
      const dt = new Date(scheduleAtLocal);
      if (Number.isNaN(dt.getTime())) {
        toast.error("Invalid schedule date");
        return;
      }
      // Min 5 minutes in future
      if (dt.getTime() - Date.now() < 5 * 60 * 1000) {
        toast.error("Schedule must be at least 5 minutes in the future");
        return;
      }
      scheduleIso = dt.toISOString();
    }
    setSubmitting(true);
    try {
      const payload = {
        name: `QuickSend · ${new Date().toISOString().slice(0, 16).replace("T", " ")}`,
        contact_list_id: effectiveListId,
        template_id: templateId,
        variable_mapping: variableMapping,
        filters: skipFilters ? {} : filters,
        duplicate_suppression_days: 0,
      };
      if (isAdmin && skipConsent) payload.skip_consent_scrub = true;
      if (scheduleIso) payload.schedule_at = scheduleIso;

      const { data } = await api.post(
        `/projects/${account.quicksend_project_id}/campaigns`, payload,
      );
      if (isAdmin && skipConsent && skipReason.trim()) {
        try {
          await api.post(`/campaigns/${data.id}/set-skip-consent`,
            { skip_consent_scrub: true, reason: skipReason.trim() });
        } catch (e) { toast.warning("Skip-consent audit failed: " + formatApiError(e)); }
      }
      await api.post(`/campaigns/${data.id}/send`);
      toast.success(scheduleIso
        ? `Scheduled for ${new Date(scheduleIso).toLocaleString()}`
        : `Sending to ${data.audience_stats.final_recipients} recipients…`);
      nav(`/campaigns/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
      setConfirmOpen(false);
    }
  };

  const sendTest = async () => {
    if (!testMobile.trim() || !effectiveListId || !templateId || !account) return;
    setSendingTest(true);
    try {
      // Create an ephemeral draft, do a test-send, then delete the draft to
      // avoid polluting the campaign list.
      const draftPayload = {
        name: `QSTest · ${new Date().toISOString().slice(0, 16)}`,
        contact_list_id: effectiveListId,
        template_id: templateId,
        variable_mapping: variableMapping,
        filters: {},
        duplicate_suppression_days: 0,
      };
      if (isAdmin && skipConsent) draftPayload.skip_consent_scrub = true;
      const { data: draft } = await api.post(
        `/projects/${account.quicksend_project_id}/campaigns`, draftPayload,
      );
      try {
        const { data: res } = await api.post(`/campaigns/${draft.id}/test-send`,
          { mobile_numbers: [testMobile.trim()] });
        toast.success(`Test SMS: ${res.per_number?.[0]?.status || "sent"}`);
      } finally {
        try { await api.delete(`/campaigns/${draft.id}`); } catch { /* ignore */ }
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSendingTest(false);
    }
  };

  if (ctxError) {
    return (
      <div className="p-8">
        <Alert variant="destructive"><AlertTitle>Could not load Quick Send</AlertTitle>
          <AlertDescription>{ctxError}</AlertDescription></Alert>
      </div>
    );
  }
  if (!ctx) return (
    <div className="p-12 text-center text-muted-foreground text-sm">
      <Loader2 className="inline h-4 w-4 animate-spin mr-2" />Loading…
    </div>
  );
  if (ctx.accounts.length === 0) {
    return (
      <div className="p-8">
        <Alert><AlertTitle>No accounts available</AlertTitle>
          <AlertDescription>Ask your admin to grant you scope to an account first.</AlertDescription></Alert>
      </div>
    );
  }

  const appliedFilterChips = Object.entries(filters)
    .filter(([, v]) => v && v.length)
    .map(([k, v]) => `${k}: ${v.join(", ")}`);

  return (
    <div className="p-8 max-w-4xl mx-auto space-y-6">
      <PageHeader
        title={<span className="flex items-center gap-2"><Zap className="h-6 w-6 text-primary" />Quick Send</span>}
        description="One-off SMS blast. Upload data → pick template → send. No project setup required."
      />

      {ctx.accounts.length > 1 && (
        <Card data-testid="qs-account-card">
          <CardHeader className="pb-3"><CardTitle className="text-sm">Account</CardTitle></CardHeader>
          <CardContent>
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger className="w-full max-w-md" data-testid="qs-account-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ctx.accounts.map(a => (
                  <SelectItem key={a.id} value={a.id}>{a.name} · sender {a.sender_id}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      )}

      {/* SECTION 1: Audience */}
      <Card data-testid="qs-audience-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Database className="h-4 w-4" />1. Choose your audience
          </CardTitle>
          <CardDescription>Upload a file, pick an existing list, or paste numbers directly.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <RadioGroup value={audienceMode} onValueChange={setAudienceMode} className="grid md:grid-cols-3 gap-3">
            <ModeCard active={audienceMode==="existing"} value="existing" testId="qs-mode-existing"
              icon={<Database className="h-4 w-4" />} title="Existing list"
              caption={`${contactLists.length} available`} />
            <ModeCard active={audienceMode==="upload"} value="upload" testId="qs-mode-upload"
              icon={<Upload className="h-4 w-4" />} title="Upload CSV / XLSX"
              caption="Up to 100 MB" />
            <ModeCard active={audienceMode==="paste"} value="paste" testId="qs-mode-paste"
              icon={<ClipboardPaste className="h-4 w-4" />} title="Paste numbers"
              caption="Max 1000 numbers" />
          </RadioGroup>

          {audienceMode === "existing" && (
            <div>
              <Label className="text-xs">Contact list</Label>
              <Select value={contactListId} onValueChange={setContactListId}>
                <SelectTrigger data-testid="qs-cl-select"><SelectValue placeholder="Pick a list" /></SelectTrigger>
                <SelectContent>
                  {contactLists.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name} · <span className="font-mono text-[11px]">{new Intl.NumberFormat("en-IN").format(c.row_count)}</span> rows
                      {c.is_ephemeral && <Badge variant="outline" className="ml-2 text-[9px]">ephemeral</Badge>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {audienceMode === "upload" && account && (
            <QuickSendUploader
              projectId={account.quicksend_project_id}
              onReady={(cid) => {
                setUploadedListId(cid);
                refreshContactLists();
                toast.success("List ready");
              }}
            />
          )}

          {audienceMode === "paste" && account && (
            <PasteNumbers
              accountId={account.id}
              onReady={(cid, meta) => {
                setPastedListId(cid);
                refreshContactLists();
                toast.success(`Ready · ${meta.row_count} valid numbers${meta.invalid_count ? ` (${meta.invalid_count} skipped)` : ""}`);
              }}
            />
          )}
        </CardContent>
      </Card>

      {/* SECTION 2: Filter (optional) — collapsed by default, shows chips */}
      {selectedList && (selectedList.filterable_columns?.length ?? 0) > 0 && (
        <Card data-testid="qs-filter-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Filter className="h-4 w-4" />2. Filter (optional)
            </CardTitle>
            <CardDescription>
              Narrow to a subset of {selectedList.row_count.toLocaleString("en-IN")} contacts.
              {appliedFilterChips.length > 0 && (
                <span className="ml-2 flex flex-wrap gap-1 mt-2">
                  {appliedFilterChips.map((c, i) => (
                    <Badge key={i} variant="secondary" className="text-[10px]" data-testid={`qs-filter-chip-${i}`}>
                      {c}
                    </Badge>
                  ))}
                </span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={skipFilters} onCheckedChange={setSkipFilters} data-testid="qs-skipfilter" />
              <span>Skip filtering — send to everyone</span>
            </label>
            {!skipFilters && (
              <div className="grid md:grid-cols-2 gap-3">
                {selectedList.filterable_columns.map(col => {
                  const options = selectedList.filter_facets?.[col] || [];
                  const val = filters[col]?.[0] || "";
                  return (
                    <div key={col}>
                      <Label className="text-xs">{col}</Label>
                      <Select value={val} onValueChange={(v) => setFilters({ ...filters, [col]: v === "__any__" ? [] : [v] })}>
                        <SelectTrigger data-testid={`qs-filter-${col}`}><SelectValue placeholder="Any" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__any__">— any —</SelectItem>
                          {options.slice(0, 50).map(o => (<SelectItem key={String(o)} value={String(o)}>{String(o)}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* SECTION 3: Template */}
      {effectiveListId && (
        <Card data-testid="qs-template-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />3. Pick a template
            </CardTitle>
            <CardDescription>DLT-registered templates for {account?.name}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger data-testid="qs-tpl-select"><SelectValue placeholder="Pick a template" /></SelectTrigger>
              <SelectContent>
                {templates.map(t => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name} <span className="ml-2 text-[10px] text-muted-foreground">[{t.category}]</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedTpl && (
              <div>
                <Label className="text-xs">Message preview</Label>
                <pre className="text-xs bg-muted rounded p-3 whitespace-pre-wrap border">{selectedTpl.body}</pre>
              </div>
            )}
            {selectedTpl?.variables?.length > 0 && (
              <div>
                <Label className="text-xs mb-2 block">Variable mapping</Label>
                <div className="space-y-2">
                  {selectedTpl.variables.map((v, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <code className="text-[11px] bg-muted px-2 py-1 rounded font-mono">{v.placeholder || `var${i}`}</code>
                      <Input
                        placeholder="static:1234 OR field:ColumnName"
                        value={variableMapping[String(i)] || ""}
                        onChange={(e) => setVariableMapping({ ...variableMapping, [String(i)]: e.target.value })}
                        data-testid={`qs-var-${i}`}
                      />
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">
                  Use <code>static:VALUE</code> for a constant, or <code>field:ColumnName</code> to pull from the row.
                </p>
              </div>
            )}
            {selectedTpl?.category === "service_explicit" && isAdmin && (
              <Alert variant={skipConsent ? "destructive" : "default"} data-testid="qs-skipconsent-alert">
                <ShieldAlert className="h-4 w-4" />
                <AlertTitle className="text-sm flex items-center justify-between">
                  <span>Skip consent scrub (admin only)</span>
                  <Switch
                    checked={skipConsent}
                    onCheckedChange={setSkipConsent}
                    data-testid="qs-skipconsent"
                  />
                </AlertTitle>
                {skipConsent && (
                  <AlertDescription className="text-xs">
                    <Textarea
                      rows={2}
                      className="mt-2"
                      placeholder="Reason (required for audit) — e.g. offline consent captured on 2026-01 forms"
                      value={skipReason}
                      onChange={(e) => setSkipReason(e.target.value)}
                      data-testid="qs-skipreason"
                    />
                  </AlertDescription>
                )}
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* SECTION 4: Schedule (optional) */}
      {effectiveListId && templateId && (
        <Card data-testid="qs-schedule-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <CalendarClock className="h-4 w-4" />4. Send now, or later
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Switch checked={scheduleEnabled} onCheckedChange={setScheduleEnabled} data-testid="qs-schedule-toggle" />
              <span>Schedule for later (Asia/Kolkata)</span>
            </label>
            {scheduleEnabled && (
              <div>
                <Label className="text-xs">Date &amp; time (min 5 min ahead)</Label>
                <Input
                  type="datetime-local"
                  value={scheduleAtLocal}
                  onChange={(e) => setScheduleAtLocal(e.target.value)}
                  data-testid="qs-schedule-at"
                  className="max-w-xs"
                />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* SECTION 5: Review + send */}
      {effectiveListId && templateId && (
        <Card className="border-primary/40" data-testid="qs-review-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Send className="h-4 w-4" />5. Review &amp; send
              {estimating && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {estimateError && (
              <Alert variant="destructive" data-testid="qs-estimate-error">
                <AlertTitle>Could not estimate audience</AlertTitle>
                <AlertDescription className="text-xs">{estimateError}</AlertDescription>
              </Alert>
            )}
            {estimate && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <MetricCard label="Matched" value={estimate.audience_stats.matched} />
                  <MetricCard
                    label="DND / consent blocked"
                    value={(estimate.audience_stats.dnd_blocked || 0) + (estimate.audience_stats.consent_blocked || 0)}
                  />
                  <MetricCard label="Will send" value={willSend} highlight />
                  <MetricCard label="Cost" value={`₹${((estimate.cost_estimate?.cost_paise || 0) / 100).toFixed(2)}`} />
                </div>

                {willSend === 0 ? (
                  <Alert data-testid="qs-empty-audience">
                    <Info className="h-4 w-4" />
                    <AlertTitle className="text-sm">No recipients match right now</AlertTitle>
                    <AlertDescription className="text-xs">
                      {estimate.audience_stats.consent_blocked > 0 && !skipConsent
                        ? "All matched contacts were blocked by consent scrub. If you have offline proof of consent, admins can toggle Skip consent scrub above."
                        : estimate.audience_stats.dnd_blocked > 0
                        ? "All matched contacts are on the DND list."
                        : "Try loosening filters or picking a different list."}
                    </AlertDescription>
                  </Alert>
                ) : (
                  <div className="flex flex-wrap gap-3 items-end pt-2 border-t">
                    <div className="flex-1 min-w-[220px]">
                      <Label className="text-xs">Send test SMS to one number</Label>
                      <div className="flex gap-2">
                        <Input placeholder="9876543210" value={testMobile}
                          onChange={(e) => setTestMobile(e.target.value)} data-testid="qs-test-mobile" />
                        <Button variant="outline" onClick={sendTest}
                          disabled={sendingTest || !testMobile.trim()} data-testid="qs-test-send">
                          {sendingTest ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <Send className="h-3 w-3 mr-1" />}
                          Send test
                        </Button>
                      </div>
                    </div>
                    <Button
                      size="lg"
                      variant="destructive"
                      onClick={() => setConfirmOpen(true)}
                      disabled={submitting || !readyToSend}
                      data-testid="qs-send-all"
                    >
                      <Zap className="h-4 w-4 mr-1.5" />
                      {scheduleEnabled ? "Schedule" : "Send"} to {willSend.toLocaleString("en-IN")} recipient{willSend !== 1 && "s"}
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="qs-confirm-dialog">
          <DialogHeader>
            <DialogTitle>{scheduleEnabled ? "Confirm schedule" : "Confirm send"}</DialogTitle>
            <DialogDescription>
              {scheduleEnabled ? "Scheduling" : "Sending"} SMS to <b>{willSend.toLocaleString("en-IN")}</b> recipients.<br />
              Estimated cost: <b>₹{((estimate?.cost_estimate?.cost_paise || 0) / 100).toFixed(2)}</b><br />
              {scheduleEnabled
                ? <>Will run at <b>{scheduleAtLocal ? new Date(scheduleAtLocal).toLocaleString() : "—"}</b>.</>
                : "This starts immediately and cannot be undone."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={doSend} disabled={submitting} data-testid="qs-confirm-send">
              {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              Yes, {scheduleEnabled ? "schedule" : "send now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ModeCard({ active, value, testId, icon, title, caption }) {
  return (
    <label className={`border rounded-lg p-4 cursor-pointer flex items-start gap-3 hover:bg-muted/50 transition ${active ? "border-primary bg-primary/5" : "border-border"}`}>
      <RadioGroupItem value={value} data-testid={testId} />
      <div>
        <div className="font-medium text-sm flex items-center gap-1">{icon} {title}</div>
        <div className="text-xs text-muted-foreground mt-1">{caption}</div>
      </div>
    </label>
  );
}

function MetricCard({ label, value, highlight = false }) {
  return (
    <div className={`rounded-lg p-3 border ${highlight ? "bg-primary/10 border-primary/40" : "bg-muted/40"}`}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-xl font-semibold font-mono tabular-nums mt-1">
        {typeof value === "number" ? new Intl.NumberFormat("en-IN").format(value) : value}
      </div>
    </div>
  );
}

// ---------------------- Inline paste-numbers ----------------------
function PasteNumbers({ accountId, onReady }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/quick-send/paste-numbers", {
        account_id: accountId,
        numbers: text,
      });
      setResult(data);
      onReady && onReady(data.id, data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3 border-2 border-dashed rounded-lg p-4" data-testid="qs-paste">
      <Label className="text-xs">Paste up to 1000 phone numbers (one per line or comma-separated)</Label>
      <Textarea
        rows={6}
        placeholder="9876543210&#10;919999999999&#10;+91 88888 88888"
        value={text}
        onChange={(e) => setText(e.target.value)}
        data-testid="qs-paste-textarea"
        className="font-mono text-xs"
      />
      <div className="flex items-center gap-3">
        <Button onClick={submit} disabled={busy || !text.trim()} data-testid="qs-paste-submit">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ClipboardPaste className="mr-2 h-4 w-4" />}
          Create ephemeral list
        </Button>
        {result && (
          <div className="text-xs text-muted-foreground">
            <b>{result.row_count}</b> valid · {result.invalid_count} skipped ·
            <span className="ml-1">auto-deletes 7 days after use</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------- Inline uploader ----------------------
function QuickSendUploader({ projectId, onReady }) {
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [phoneCol, setPhoneCol] = useState("");
  const [filterCols, setFilterCols] = useState({});
  const [busy, setBusy] = useState(false);
  const [parsingId, setParsingId] = useState(null);
  const [parseStatus, setParseStatus] = useState(null);

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setBusy(true);
    setPreviewData(null);
    setParsingId(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(
        `/projects/${projectId}/contact-lists/preview`, fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setPreviewData(data);
      const phoneGuess = (data.detected_columns || []).find(
        c => /^(phone|mobile|contact|number)$/i.test(c),
      );
      if (phoneGuess) setPhoneCol(phoneGuess);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    if (!phoneCol || !previewData) return;
    setBusy(true);
    try {
      const filterable = Object.entries(filterCols).filter(([, v]) => v).map(([k]) => k);
      const { data } = await api.post(`/projects/${projectId}/contact-lists`, {
        name: file.name.replace(/\.(csv|xlsx?|txt)$/i, "") + ` · ${new Date().toISOString().slice(0, 16)}`,
        gridfs_id: previewData.gridfs_id,
        source_filename: file.name,
        source_bytes: previewData.source_bytes,
        phone_column: phoneCol,
        filterable_columns: filterable,
        duplicate_policy: "keep_first",
        country_code_default: "91",
      });
      setParsingId(data.id);
      poll(data.id);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const poll = (id) => {
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get(`/contact-lists/${id}/status`);
        setParseStatus(data);
        if (data.status === "ready") {
          clearInterval(iv);
          onReady && onReady(id);
        } else if (data.status === "failed") {
          clearInterval(iv);
          toast.error("Parse failed: " + (data.parse_error || "unknown"));
        }
      } catch { /* keep polling */ }
    }, 2000);
  };

  if (parsingId) {
    const pct = parseStatus?.progress?.percent || 0;
    const rows = parseStatus?.progress?.rows_processed || 0;
    const status = parseStatus?.status || "parsing";
    return (
      <div className="space-y-2" data-testid="qs-parse-progress">
        <div className="flex items-center justify-between text-xs">
          <span>{status === "ready" ? <><CheckCircle2 className="inline h-3 w-3 mr-1 text-emerald-500" />Ready</> : `Parsing… ${new Intl.NumberFormat("en-IN").format(rows)} rows`}</span>
          <span className="font-mono">{status === "ready" ? "100%" : (pct ? `${pct.toFixed(0)}%` : "…")}</span>
        </div>
        <Progress value={status === "ready" ? 100 : pct} />
      </div>
    );
  }

  return (
    <div className="space-y-3 border-2 border-dashed rounded-lg p-4" data-testid="qs-uploader">
      <div>
        <Label className="text-xs">CSV or XLSX file</Label>
        <Input type="file" accept=".csv,.xlsx,.xls" onChange={onFileChange} disabled={busy} data-testid="qs-file-input" />
      </div>
      {busy && !previewData && <div className="text-xs text-muted-foreground"><Loader2 className="inline h-3 w-3 animate-spin mr-1" />Reading first 200 rows…</div>}
      {previewData && (
        <>
          <div className="text-xs text-muted-foreground">
            Preview: <b>{previewData.row_count_estimate?.toLocaleString("en-IN")}</b> rows estimated, {previewData.detected_columns?.length} columns detected.
          </div>
          <div>
            <Label className="text-xs">Phone column</Label>
            <Select value={phoneCol} onValueChange={setPhoneCol}>
              <SelectTrigger data-testid="qs-phonecol-select"><SelectValue placeholder="Which column has the phone number?" /></SelectTrigger>
              <SelectContent>
                {previewData.detected_columns?.map(c => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Filterable columns <span className="text-muted-foreground">(optional)</span></Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-1">
              {previewData.detected_columns?.filter(c => c !== phoneCol).map(c => (
                <label key={c} className="flex items-center gap-2 text-xs cursor-pointer">
                  <Checkbox
                    checked={!!filterCols[c]}
                    onCheckedChange={(v) => setFilterCols({ ...filterCols, [c]: !!v })}
                    data-testid={`qs-filter-col-${c}`}
                  />
                  <span>{c}</span>
                </label>
              ))}
            </div>
          </div>
          <Button onClick={commit} disabled={busy || !phoneCol} data-testid="qs-start-import">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
            Start import
          </Button>
        </>
      )}
    </div>
  );
}
